// Generate a GeoJSON polygon circle in meters around a point

import { useMemo } from "react";
import { mapSettings } from "./mapSettings";
import { usePosition } from "src/api/hooks/usePosition";
import { Marker, Source, Layer} from "react-map-gl";

function createCirclePolygon(
    centerLng: number,
    centerLat: number,
    radiusMeters: number,
    numPoints: number = 64
  ): { type: "Polygon"; coordinates: [number, number][][] } {
    const coordinates: [number, number][] = [];
    
    // Earth's radius in meters
    const earthRadius = 6378137;
    const centerLatRad = (centerLat * Math.PI) / 180;
    const centerLngRad = (centerLng * Math.PI) / 180;
    
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      
      // Calculate point at given bearing and distance using spherical geometry
      const angularDistance = radiusMeters / earthRadius;
      const bearing = angle;
      
      const latRad = Math.asin(
        Math.sin(centerLatRad) * Math.cos(angularDistance) +
          Math.cos(centerLatRad) * Math.sin(angularDistance) * Math.cos(bearing)
      );
      
      const lngRad =
        centerLngRad +
        Math.atan2(
          Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(centerLatRad),
          Math.cos(angularDistance) - Math.sin(centerLatRad) * Math.sin(latRad)
        );
      
      const lat = (latRad * 180) / Math.PI;
      const lng = (lngRad * 180) / Math.PI;
      
      coordinates.push([lng, lat]);
    }
    
    // Close the polygon
    coordinates.push(coordinates[0]);
    
    return {
      type: "Polygon",
      coordinates: [coordinates],
    };
  }
  
  // User/Profile SVG Icon Component
  function UserIcon() {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="12"
          cy="12"
          r="8"
          fill={mapSettings.userLocationColor}
          stroke="white"
          strokeWidth="2"
        />
        
      </svg>
    );
  }
  
  export function UserPositionMarker() {
    const userPosition = usePosition();
  
    // Generate circle polygon in meters based on accuracy
    const circlePolygon = useMemo(() => {
      if (!userPosition.longitude || !userPosition.latitude || !userPosition.accuracy) {
        return null;
      }
      
      return createCirclePolygon(
        userPosition.longitude,
        userPosition.latitude,
        userPosition.accuracy
      );
    }, [userPosition.longitude, userPosition.latitude, userPosition.accuracy]);
  
    if (!userPosition.longitude || !userPosition.latitude || !circlePolygon) {
      return null;
    }
  
    return (
      <>
        <Marker
          longitude={userPosition.longitude}
          latitude={userPosition.latitude}
          anchor="center"
        >
          <UserIcon />
        </Marker>
        <Source
          id="user-position-circle"
          type="geojson"
          data={{
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                geometry: circlePolygon,
              },
            ],
          }}
        >
          <Layer
            id="user-position-circle-layer"
            type="fill"
            paint={{
              "fill-color": mapSettings.userLocationColor,
              "fill-opacity": 0.2,
            }}
          />
          <Layer
            id="user-position-circle-stroke"
            type="line"
            paint={{
              "line-color": mapSettings.userLocationColor,
              "line-width": 2,
              "line-opacity": 0.5,
            }}
          />
        </Source>
      </>
    );
  }